import { type IOctToastComponent } from "@components/oct-toast/oct-toast.component";
import { firstValueFrom, Subject, take } from "rxjs";

import { Service, signal, type TemplateRef } from "@angular/core";

export interface IToastState extends IOctToastComponent {
  templateRef: TemplateRef<unknown> | null;
  templateContext: unknown | null;
}

@Service()
export class ToastService {
  toastState = signal<IToastState>({
    type: "info",
    compact: false,
    header: "",
    content: "",
    askConfirmation: false,
    shown: false,
    confirmText: "",
    cancelText: "",
    top: "16px",
    width: "440px",
    personalizedContent: false,
    templateRef: null,
    templateContext: null,
  });

  private readonly toastEvent = new Subject<boolean>();

  async showToast(toast: Partial<IOctToastComponent>, durationMs = 2500) {
    const currentState = this.toastState();

    if (currentState.shown) {
      this.hideToast();
      await new Promise(resolve => setTimeout(resolve, 210));
    }

    const finalToast = {
      ...toast,
      personalizedContent: false,
      askConfirmation: toast.askConfirmation ?? true,
      shown: true,
      templateRef: null,
      templateContext: null,
    };

    this.toastState.update(state => ({
      ...state,
      ...finalToast,
    }));

    if (finalToast.compact) {
      setTimeout(() => this.hideToast(), durationMs);
      return false;
    }

    return this.waitForResponse();
  }

  async showTemplateToast(
    toast: Partial<IOctToastComponent>,
    templateRef: TemplateRef<unknown>,
    context?: unknown
  ) {
    const currentState = this.toastState();

    if (currentState.shown) {
      this.hideToast();
      await new Promise(resolve => setTimeout(resolve, 210));
    }

    this.toastState.update(state => ({
      ...state,
      ...toast,
      personalizedContent: true,
      askConfirmation: toast.askConfirmation ?? true,
      shown: true,
      templateRef,
      templateContext: context ? { $implicit: context } : {},
    }));

    return this.waitForResponse();
  }

  private async waitForResponse() {
    try {
      const result = await firstValueFrom(this.toastEvent.pipe(take(1)));
      this.hideToast();
      return result;
    } catch (error) {
      this.hideToast();
      throw error;
    }
  }

  private hideToast() {
    this.toastState.update(state => ({ ...state, shown: false }));
  }

  async confirmDeletion(header: string) {
    return this.showToast({
      type: "error",
      compact: false,
      askConfirmation: true,
      header,
      content: "Esta acción no se puede deshacer.",
      confirmText: "Eliminar",
      cancelText: "Cancelar",
    });
  }

  showCompactToast(
    type: "success" | "error" | "warning" | "info",
    header: string,
    durationMs = 2500
  ) {
    this.showToast(
      {
        type,
        header,
        compact: true,
        personalizedContent: false,
      },
      durationMs
    );
  }

  respond(response: boolean) {
    this.toastEvent.next(response);
    this.hideToast();
  }
}
